// Native Google Sign In helper for the iOS Capacitor wrap.
//
// The web GoogleLogin component from @react-oauth/google does not render
// inside WKWebView (Google's web SDK refuses, by design). On native we use
// @codetrix-studio/capacitor-google-auth, which calls the iOS GoogleSignIn
// SDK and returns an idToken we can ship to POST /auth/google.
//
// The iOS GoogleSignIn SDK treats the `clientId` we pass to initialize() as
// the iOS OAuth client (the one the OAuth flow runs against, with a matching
// reversed-client-ID URL scheme registered in Info.plist). The idToken's
// audience claim ends up being that iOS client ID. So the backend has to
// accept either the web client ID (browser logins) or the iOS client ID
// (native logins) — see GOOGLE_CLIENT_ID + GOOGLE_CLIENT_ID_IOS on the server.

let initialized = false;

// OAuth client IDs are public application identifiers, not secrets. Keep the
// Bundle-ID-bound production client as a fallback so a local App Store archive
// cannot silently ship a broken Google button because a Vite env var was
// omitted. The env override remains available for alternate signed builds.
const GOOGLE_CLIENT_ID_IOS =
  import.meta.env.VITE_GOOGLE_CLIENT_ID_IOS
  || '590547616252-fhf6kns42rdsj8q1hmm3g2q8l8uqma05.apps.googleusercontent.com';

export async function signInWithGoogleNative(): Promise<string> {
  // Dynamic import so the web bundle doesn't pull in the native plugin.
  const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');

  if (!initialized) {
    const clientId = GOOGLE_CLIENT_ID_IOS;
    if (!clientId) {
      throw new Error(
        'Google sign-in is not configured. VITE_GOOGLE_CLIENT_ID_IOS is missing.',
      );
    }
    await GoogleAuth.initialize({
      clientId,
      scopes: ['profile', 'email'],
      grantOfflineAccess: true,
    });
    initialized = true;
  }

  const result = await GoogleAuth.signIn();
  const idToken = result.authentication?.idToken;
  if (!idToken) {
    throw new Error('Google sign-in returned no idToken.');
  }
  return idToken;
}
